<script setup>
import { ref, useTemplateRef, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'conversation',
  },
  action: {
    type: String,
    default: 'assign',
    validator: value => ['assign', 'remove'].includes(value),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  appliedLabels: {
    type: Array,
    default: null,
  },
  showButtonLabel: {
    type: Boolean,
    default: undefined,
  },
  mobileButtonLabel: {
    type: String,
    default: '',
  },
  // Where the menu opens relative to the button. Bars at the top of a scrolling list must open downward.
  placement: {
    type: String,
    default: 'top',
    validator: value => ['top', 'bottom'].includes(value),
  },
});

const emit = defineEmits(['assign', 'remove']);

const { t } = useI18n();

const labels = useMapGetter('labels/getLabels');

const containerRef = useTemplateRef('containerRef');
const [showDropdown, toggleDropdown] = useToggle(false);
const selectedLabels = ref([]);

const isTypeContact = computed(() => props.type === 'contact');
const isRemoveAction = computed(() => props.action === 'remove');
const opensDownward = computed(() => props.placement === 'bottom');

const shouldShowButtonLabel = computed(() => {
  if (props.showButtonLabel !== undefined) return props.showButtonLabel;
  return isTypeContact.value;
});

const buttonLabel = computed(() => {
  if (!isTypeContact.value) return '';

  return isRemoveAction.value
    ? t('BULK_ACTION.CONTACT_LABELS.REMOVE_LABELS')
    : t('BULK_ACTION.CONTACT_LABELS.ASSIGN_LABELS');
});

const tooltipLabel = computed(() => {
  if (isTypeContact.value) {
    return isRemoveAction.value
      ? t('BULK_ACTION.CONTACT_LABELS.REMOVE_LABELS')
      : t('BULK_ACTION.CONTACT_LABELS.ASSIGN_LABELS');
  }
  return isRemoveAction.value
    ? t('BULK_ACTION.LABELS.REMOVE_LABELS')
    : t('BULK_ACTION.LABELS.ASSIGN_LABELS');
});

const confirmLabel = computed(() => {
  if (isTypeContact.value) {
    return isRemoveAction.value
      ? t('BULK_ACTION.CONTACT_LABELS.REMOVE_SELECTED_LABELS')
      : t('BULK_ACTION.CONTACT_LABELS.ASSIGN_SELECTED_LABELS');
  }
  return isRemoveAction.value
    ? t('BULK_ACTION.LABELS.REMOVE_SELECTED_LABELS')
    : t('BULK_ACTION.LABELS.ASSIGN_SELECTED_LABELS');
});

const isLabelSelected = labelTitle => {
  return selectedLabels.value.includes(labelTitle);
};

const visibleLabels = computed(() => {
  if (!isRemoveAction.value || props.appliedLabels === null) {
    return labels.value;
  }

  const applied = new Set(props.appliedLabels);
  return labels.value.filter(label => applied.has(label.title));
});

const labelMenuItems = computed(() => {
  return visibleLabels.value.map(label => ({
    action: 'select',
    value: label.title,
    label: label.title,
    color: label.color,
    id: label.id,
    isSelected: isLabelSelected(label.title),
  }));
});

const toggleLabelSelection = labelTitle => {
  const index = selectedLabels.value.indexOf(labelTitle);
  if (index > -1) {
    selectedLabels.value.splice(index, 1);
  } else {
    selectedLabels.value.push(labelTitle);
  }
};

const handleApply = () => {
  if (selectedLabels.value.length > 0) {
    if (isRemoveAction.value) {
      emit('remove', selectedLabels.value);
    } else {
      emit('assign', selectedLabels.value);
    }
    toggleDropdown(false);
    selectedLabels.value = [];
  }
};

const handleDismiss = () => {
  selectedLabels.value = [];
  toggleDropdown(false);
};
</script>

<template>
  <div ref="containerRef" class="relative">
    <NextButton
      v-tooltip="tooltipLabel"
      :label="shouldShowButtonLabel ? buttonLabel : ''"
      :title="tooltipLabel"
      :aria-label="tooltipLabel"
      :icon="
        isTypeContact
          ? isRemoveAction
            ? 'i-lucide-user-round-x'
            : 'i-lucide-contact'
          : isRemoveAction
            ? 'i-woot-tag-remove'
            : 'i-lucide-tag'
      "
      slate
      :size="shouldShowButtonLabel ? 'sm' : 'xs'"
      ghost
      class="transition-all duration-150 ease-out active:scale-95"
      :class="{
        'bg-n-alpha-2': showDropdown,
        '[&>span]:!text-n-blue-11': !shouldShowButtonLabel,
        '[&>span:nth-child(2)]:hidden md:[&>span:nth-child(2)]:inline w-fit !text-n-blue-11 [&>span]:!text-n-blue-11 !px-2':
          shouldShowButtonLabel,
      }"
      :disabled="disabled || isLoading"
      :is-loading="isLoading"
      @click.stop="toggleDropdown()"
    >
      <template v-if="shouldShowButtonLabel" #default>
        <span class="hidden whitespace-nowrap md:inline-flex">{{
          buttonLabel
        }}</span>
        <span class="whitespace-nowrap md:hidden">{{
          mobileButtonLabel || buttonLabel
        }}</span>
      </template>
    </NextButton>
    <Transition
      enter-active-class="transition-all duration-150 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition-all duration-100 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <DropdownMenu
        v-if="showDropdown"
        v-on-click-outside="[handleDismiss, { ignore: [containerRef] }]"
        :menu-items="labelMenuItems"
        show-search
        compact
        :search-placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
        class="ltr:right-0 rtl:left-0 w-60 max-h-80"
        :class="opensDownward ? 'top-8 origin-top' : 'bottom-8 origin-bottom'"
        @action="item => toggleLabelSelection(item.value)"
      >
        <template #label="{ item }">
          <Label
            :label="{ title: item.label, color: item.color }"
            compact
            class="!min-w-0 !max-w-full !shrink [&>span]:truncate"
          />
        </template>

        <template #trailing-icon="{ item }">
          <Icon
            v-if="isLabelSelected(item.value)"
            icon="i-lucide-check"
            class="size-3.5 text-n-blue-11 flex-shrink-0 ms-auto"
          />
        </template>

        <template #footer>
          <div
            class="sticky bottom-0 rounded-b-md px-1 py-1.5 z-20 bg-n-alpha-3 backdrop-blur-[4px]"
          >
            <NextButton
              sm
              class="w-full"
              :label="confirmLabel"
              :disabled="!selectedLabels.length"
              @click="handleApply"
            />
          </div>
        </template>
      </DropdownMenu>
    </Transition>
  </div>
</template>
